import { ASSETS_AND_TEST_CONTAINER_ID } from '@ardoq/icons';
import { confirmLargeFile, getClonedDOM } from './exportUtil';
import { isSafari, sleep } from '@ardoq/common-helpers';
import type {
  ExportLabels,
  ExportTrackingData,
  GetStyleSheetsFn,
} from './types';
import { ViewIds } from '@ardoq/api-types';
import {
  CANVAS_MAXIMUM_AREA,
  CANVAS_MAXIMUM_DIMENSION,
  HTML_EXPORT_PADDING,
  VIEW_LEGEND_SELECTOR,
} from './consts';
import * as profiling from '@ardoq/profiling';
import { drawImageToCanvas } from './drawImageToCanvasListener';
import { viewExporters } from './viewExporters/viewExporters';
import type {
  ExportedView,
  ExportedViewMeasurements,
} from './viewExporters/types';
import {
  getStyleElementHtml,
  getStyles,
  serializeAndWrapInSVG,
} from './htmlExport';
import { isExportCapturing, toExportTooLargeError } from './exportSink';

const EXPORT_MAXIMUM_DIMENSION = CANVAS_MAXIMUM_DIMENSION - HTML_EXPORT_PADDING;
const EXPORT_MAXIMUM_AREA = CANVAS_MAXIMUM_AREA - HTML_EXPORT_PADDING ** 2;

const measureView = (element: Element) => {
  const testContainer = document.getElementById(ASSETS_AND_TEST_CONTAINER_ID);
  if (!testContainer) {
    return { width: 0, height: 0 };
  }
  testContainer.appendChild(element);
  const { scrollWidth: width, scrollHeight: height } = element;
  const viewContent = Array.from(
    element.querySelectorAll<HTMLElement>(VIEW_LEGEND_SELECTOR)
  );
  const viewContentMaxWidth = Math.max(
    ...viewContent.map(viewContentElement => viewContentElement.scrollWidth)
  );
  const viewContentMaxHeight = Math.max(
    ...viewContent.map(viewContentElement => viewContentElement.scrollHeight)
  );
  testContainer.removeChild(element);
  return {
    width,
    height,
    viewContentMaxWidth,
    viewContentMaxHeight,
  };
};
type GetExportedCanvasArgs = {
  width: number;
  height: number;
  clonedDOM: HTMLElement;
  getStyleSheets: GetStyleSheetsFn;
  css: string;
  defs: string;
  profilingTransaction: profiling.Transaction;
};
const toDecodeFailure = (error: unknown, isOversized: boolean): unknown =>
  isOversized && error instanceof DOMException && error.name === 'EncodingError'
    ? toExportTooLargeError(`Export image failed to decode: ${error.message}`)
    : error;

const getExportedCanvas = async ({
  width: domWidth,
  height: domHeight,
  clonedDOM,
  getStyleSheets,
  css,
  defs,
  profilingTransaction,
}: GetExportedCanvasArgs) => {
  const canvas = document.createElement('canvas');
  canvas.id = 'canvasExport';

  // #region limit canvas dimensions
  const area = domWidth * domHeight;
  const maxDimension = Math.max(domWidth, domHeight);
  const dimensionLimitedScale =
    maxDimension > EXPORT_MAXIMUM_DIMENSION
      ? EXPORT_MAXIMUM_DIMENSION / maxDimension
      : 1;
  const areaLimitedScale =
    area > EXPORT_MAXIMUM_AREA ? Math.sqrt(EXPORT_MAXIMUM_AREA / area) : 1;
  // #endregion

  const scale = Math.min(1, dimensionLimitedScale, areaLimitedScale);
  const imageWidth = domWidth * scale;
  const imageHeight = domHeight * scale;
  const padding = (scale * HTML_EXPORT_PADDING) / 2;
  const width = imageWidth + 2 * padding;
  const height = imageHeight + 2 * padding;
  canvas.width = width;
  canvas.height = height;

  const img = new Image();
  img.crossOrigin = 'anonymous';

  const serializedStyleElement = css
    ? getStyleElementHtml(css)
    : await getStyles(clonedDOM, getStyleSheets, css, profilingTransaction);
  const wrappedInSVG = serializeAndWrapInSVG(
    profilingTransaction,
    clonedDOM,
    domWidth,
    domHeight,
    `${serializedStyleElement}${defs}`
  );
  img.src = `data:image/svg+xml,${encodeURIComponent(wrappedInSVG)}`;
  await img.decode().catch((error: unknown) => {
    throw toDecodeFailure(error, scale < 1);
  });
  if (isSafari()) {
    // Safari raises image.onload before the fonts embedded in the SVG URI are loaded. so we need to wait for some async operations to complete.
    // https://stackoverflow.com/questions/63940595/safari-svg-to-canvas-with-embedded-font
    await sleep(0);
  }
  drawImageToCanvas({
    img,
    canvas,
    width,
    height,
    padding,
  });
  return canvas;
};

const applyMeasurements = (
  measurements: ExportedViewMeasurements,
  element: HTMLElement
) => {
  let { width, height } = measurements;
  const { viewContentMaxWidth = 0, viewContentMaxHeight = 0 } = measurements;
  if (viewContentMaxWidth > width) {
    width = viewContentMaxWidth;
    element.style.width = `${viewContentMaxWidth}px`;
  }
  if (viewContentMaxHeight > height) {
    height = viewContentMaxHeight;
    element.style.height = `${height}px`;
  }
  return { width, height };
};
/**
 * pops up a confirm dialog if the given size exceeds the limitations of {@link CanvasRenderingContext2D.drawImage} and {@link HTMLCanvasElement.toBlob}
 * @returns true if the image doesn't exceed the size limitations, or if the user confirmed that it's okay and size doesn't matter. otherwise, false.
 */
const confirmIfLarge = async (
  width: number,
  height: number,
  labels?: ExportLabels
) => {
  const area = width * height;
  const isLargeImage =
    width > CANVAS_MAXIMUM_DIMENSION ||
    height > CANVAS_MAXIMUM_DIMENSION ||
    area > CANVAS_MAXIMUM_AREA;
  return (
    !isLargeImage || isExportCapturing() || (await confirmLargeFile(labels))
  );
};

const defaultExportedView = async (
  containerElement: HTMLElement,
  viewType: string,
  mutableTrackingData: ExportTrackingData,
  profilingTransaction: profiling.Transaction
): Promise<ExportedView> => {
  const clonedDOM = await getClonedDOM(
    containerElement,
    viewType,
    mutableTrackingData,
    profilingTransaction
  );
  const measurements = measureView(clonedDOM);
  return { clonedDOM, measurements, css: '', defs: '' };
};

export const htmlToCanvas = async (
  containerElement: HTMLElement,
  viewType: string,
  getStyleSheets: GetStyleSheetsFn,
  mutableTrackingData: ExportTrackingData,
  profilingTransaction: profiling.Transaction,
  isDiscoverViewpoint?: boolean,
  labels?: ExportLabels
): Promise<HTMLCanvasElement | null> => {
  const exportedView =
    (isDiscoverViewpoint && viewType === ViewIds.DEPENDENCY_MAP_2
      ? null
      : await viewExporters[viewType]?.(
          containerElement,
          getStyleSheets,
          labels
        )) ??
    (await defaultExportedView(
      containerElement,
      viewType,
      mutableTrackingData,
      profilingTransaction
    ));
  const { clonedDOM, measurements, css, defs } = exportedView;
  const { width, height } = applyMeasurements(measurements, clonedDOM);
  if (await confirmIfLarge(width, height, labels)) {
    return getExportedCanvas({
      width,
      height,
      clonedDOM,
      getStyleSheets,
      css,
      defs,
      profilingTransaction,
    });
  }
  return null;
};
